import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface Endpoint {
  method: Method;
  path: string;
  description: string;
  auth: "admin" | "client" | "none" | "api_key";
  body?: string;
  response?: string;
}

interface EndpointGroup {
  title: string;
  description: string;
  endpoints: Endpoint[];
}

const methodColors: Record<Method, string> = {
  GET: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  POST: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  PATCH: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  DELETE: "bg-red-500/10 text-red-600 border-red-500/20",
};

const apiGroups: EndpointGroup[] = [
  {
    title: "Autentisering - Admin",
    description: "Inloggning och sessionshantering for administratorer. Sessioner hanteras med cookies.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin-auth/exists",
        description: "Kontrollera om admin-konto finns",
        auth: "none",
        response: `{ "exists": boolean }`,
      },
      {
        method: "POST",
        path: "/api/admin-auth/init",
        description: "Initiera admin-konto (dev-läge eller ALLOW_ADMIN_INIT=true)",
        auth: "none",
        response: `{ "message": "Admin initialized" }`,
      },
      {
        method: "POST",
        path: "/api/admin-auth/login",
        description: "Logga in som admin",
        auth: "none",
        body: `{ "username": "string", "password": "string" }`,
        response: `{ "message": "Login successful", "user": { "id": "string", "username": "string", "displayName": "string" } }`,
      },
      {
        method: "POST",
        path: "/api/admin-auth/logout",
        description: "Logga ut admin-session",
        auth: "admin",
        response: `{ "message": "Logged out" }`,
      },
      {
        method: "GET",
        path: "/api/admin-auth/me",
        description: "Hämta aktuell admin-session",
        auth: "admin",
        response: `{ "id": "string", "username": "string", "displayName": "string" }`,
      },
      {
        method: "POST",
        path: "/api/admin-auth/change-password",
        description: "Ändra admin-lösenord",
        auth: "admin",
        body: `{ "currentPassword": "string", "newPassword": "string" }`,
        response: `{ "message": "Password changed" }`,
      },
    ],
  },
  {
    title: "Autentisering - Kund",
    description: "Inloggning och sessionshantering för kunder.",
    endpoints: [
      {
        method: "POST",
        path: "/api/client-auth/login",
        description: "Logga in som kund",
        auth: "none",
        body: `{ "username": "string", "password": "string" }`,
        response: `{ "message": "Login successful", "user": { "id": "string", "clientId": "string", "name": "string" } }`,
      },
      {
        method: "POST",
        path: "/api/client-auth/logout",
        description: "Logga ut kund-session",
        auth: "client",
        response: `{ "message": "Logged out" }`,
      },
      {
        method: "GET",
        path: "/api/client-auth/me",
        description: "Hämta aktuell kund-session",
        auth: "client",
        response: `{ "id": "string", "clientId": "string", "name": "string", "client": { ... } }`,
      },
    ],
  },
  {
    title: "Produkter",
    description: "Hantera produktkatalogen. Publika och admin-endpunkter.",
    endpoints: [
      {
        method: "GET",
        path: "/api/products",
        description: "Hämta alla aktiva produkter (publik)",
        auth: "none",
        response: `[{ "id": "string", "name": "string", "description": "string", "unitPrice": "string", "sellUnit": "pcs|bag|box", "weightGrams": "string", "pcsPerBag": number, "bagsPerBox": number, "isActive": boolean, "isNew": boolean, "imageUrl": "string|null" }]`,
      },
      {
        method: "GET",
        path: "/api/admin/products",
        description: "Hämta alla produkter (inkl. inaktiva)",
        auth: "admin",
        response: `[{ ...product }]`,
      },
      {
        method: "POST",
        path: "/api/admin/products",
        description: "Skapa ny produkt",
        auth: "admin",
        body: `{ "name": "string", "description": "string", "unitPrice": "string", "sellUnit": "pcs|bag|box", "weightGrams": "string", "pcsPerBag": number, "bagsPerBox": number, "isActive": true, "isNew": false }`,
        response: `{ ...product }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/products/:id",
        description: "Uppdatera produkt",
        auth: "admin",
        body: `{ "name?": "string", "unitPrice?": "string", ... }`,
        response: `{ ...product }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/products/:id",
        description: "Radera produkt",
        auth: "admin",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    title: "Kunder",
    description: "Hantera kunder och deras inloggningsuppgifter.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/clients",
        description: "Hämta alla kunder",
        auth: "admin",
        response: `[{ "id": "string", "name": "string", "address": "string", "phone": "string", "email": "string", "organizationNumber": "string", "isActive": boolean }]`,
      },
      {
        method: "POST",
        path: "/api/admin/clients",
        description: "Skapa ny kund",
        auth: "admin",
        body: `{ "name": "string", "address": "string", "phone": "string", "email": "string", "organizationNumber": "string" }`,
        response: `{ ...client }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/clients/:id",
        description: "Uppdatera kund",
        auth: "admin",
        body: `{ "name?": "string", "address?": "string", ... }`,
        response: `{ ...client }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/clients/:id",
        description: "Radera kund",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "POST",
        path: "/api/admin/clients/:id/credentials",
        description: "Skapa/uppdatera kundens inloggningsuppgifter",
        auth: "admin",
        body: `{ "username": "string", "password": "string" }`,
        response: `{ "message": "Credentials saved" }`,
      },
      {
        method: "GET",
        path: "/api/admin/clients/:id/credentials",
        description: "Hämta kundens inloggningsuppgifter",
        auth: "admin",
        response: `{ "username": "string" }`,
      },
    ],
  },
  {
    title: "Ordrar",
    description: "Skapa och hantera ordrar. Kunder kan skapa ordrar, admin kan hantera alla.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/stats",
        description: "Hämta dashboard-statistik",
        auth: "admin",
        response: `{ "totalOrders": number, "totalClients": number, "totalProducts": number, "pendingOrders": number }`,
      },
      {
        method: "POST",
        path: "/api/orders",
        description: "Skapa ny order (kund)",
        auth: "client",
        body: `{ "items": [{ "productId": "string", "quantity": number }], "deliveryDate": "string|null", "notes": "string|null" }`,
        response: `{ ...order, items: [...] }`,
      },
      {
        method: "GET",
        path: "/api/orders/my",
        description: "Hämta kundens egna ordrar",
        auth: "client",
        response: `[{ "id": "string", "orderNumber": number, "status": "pending|confirmed|in_production|packed|delivered", "totalAmount": "string", "orderDate": "string", "items": [...], "client": {...} }]`,
      },
      {
        method: "GET",
        path: "/api/admin/orders",
        description: "Hämta alla ordrar",
        auth: "admin",
        response: `[{ ...order, items: [...], client: {...} }]`,
      },
      {
        method: "GET",
        path: "/api/admin/orders/:id",
        description: "Hämta specifik order",
        auth: "admin",
        response: `{ ...order, items: [...], client: {...} }`,
      },
      {
        method: "POST",
        path: "/api/admin/orders",
        description: "Skapa order som admin",
        auth: "admin",
        body: `{ "clientId": "string", "items": [{ "productId": "string", "quantity": number }], "deliveryDate": "string|null", "notes": "string|null" }`,
        response: `{ ...order }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/orders/:id",
        description: "Uppdatera order (status och/eller artiklar). Status: pending, confirmed, in_production, packed, delivered, levererad. Sätter deliveryDate automatiskt vid delivered/levererad.",
        auth: "admin",
        body: `{ "status": "pending|confirmed|in_production|packed|delivered|levererad", "items": [{ "productId": "string", "quantity": number }] }`,
        response: `{ ...order, items: [...] }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/orders/:id/status",
        description: "Dedikerat endpoint för att ändra orderstatus (för externa system). Sätter deliveryDate automatiskt vid delivered/levererad.",
        auth: "admin",
        body: `{ "status": "pending|confirmed|in_production|packed|delivered|levererad" }`,
        response: `{ ...order, items: [...] }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/orders/:id/edit",
        description: "Redigera orderinnehåll (alla statusar)",
        auth: "admin",
        body: `{ "items": [{ "productId": "string", "quantity": number }], "notes": "string|null", "status": "string" }`,
        response: `{ ...order, items: [...] }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/orders/:id",
        description: "Radera order",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "GET",
        path: "/api/admin/invoiced-order-ids",
        description: "Hämta ID:n för fakturerade ordrar (admin)",
        auth: "admin",
        response: `["order-id-1", "order-id-2"]`,
      },
      {
        method: "GET",
        path: "/api/invoiced-order-ids",
        description: "Hämta ID:n för fakturerade ordrar (kund, egna ordrar)",
        auth: "client",
        response: `["order-id-1", "order-id-2"]`,
      },
    ],
  },
  {
    title: "Produktion",
    description: "Hantera produktionsflödet: Trågare → Bagare → Pakare.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/production/active",
        description: "Hämta aktiv produktionssession",
        auth: "admin",
        response: `{ "id": "string", "status": "active|completed", "startedAt": "string" } | null`,
      },
      {
        method: "POST",
        path: "/api/admin/production/start",
        description: "Starta produktion (valfritt: ange specifika orderID:n)",
        auth: "admin",
        body: `{ "orderIds?": ["string"] }`,
        response: `{ ...productionSession }`,
      },
      {
        method: "POST",
        path: "/api/admin/production/cancel",
        description: "Avbryt pågående produktion",
        auth: "admin",
        response: `{ "message": "Production cancelled" }`,
      },
      {
        method: "GET",
        path: "/api/production/active",
        description: "Hämta aktiv produktion (för produktionsvyer)",
        auth: "none",
        response: `{ ...productionSession } | null`,
      },
      {
        method: "GET",
        path: "/api/production/totals",
        description: "Hämta produktionstotaler per produkt",
        auth: "none",
        response: `[{ "productId": "string", "productName": "string", "totalQuantity": number, "unit": "string", "traglesProgress": number, "bagarProgress": number }]`,
      },
      {
        method: "POST",
        path: "/api/production/progress/:productId/:role",
        description: "Uppdatera produktionsframsteg för en produkt",
        auth: "none",
        body: `{ "quantity": number, "workerId": "string" }`,
        response: `{ "success": true }`,
      },
      {
        method: "GET",
        path: "/api/production/orders",
        description: "Hämta ordrar i aktiv produktion",
        auth: "none",
        response: `[{ ...order, items: [...], client: {...} }]`,
      },
      {
        method: "GET",
        path: "/api/production/orders/:id",
        description: "Hämta specifik produktionsorder",
        auth: "none",
        response: `{ ...order, items: [...], client: {...} }`,
      },
      {
        method: "POST",
        path: "/api/production/orders/:id/pack",
        description: "Markera order som packad",
        auth: "none",
        body: `{ "workerId": "string" }`,
        response: `{ ...order }`,
      },
      {
        method: "POST",
        path: "/api/production/finish",
        description: "Avsluta produktion",
        auth: "none",
        response: `{ "message": "Production finished" }`,
      },
    ],
  },
  {
    title: "Returer",
    description: "Hantera produktreturer.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/returns",
        description: "Hämta alla returer",
        auth: "admin",
        response: `[{ "id": "string", "clientId": "string", "productId": "string", "quantity": number, "reason": "string", "returnDate": "string", "client": {...}, "product": {...} }]`,
      },
      {
        method: "POST",
        path: "/api/admin/returns",
        description: "Skapa retur",
        auth: "admin",
        body: `{ "clientId": "string", "productId": "string", "quantity": number, "reason": "string" }`,
        response: `{ ...return }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/returns/:id",
        description: "Uppdatera retur",
        auth: "admin",
        body: `{ "quantity?": number, "reason?": "string" }`,
        response: `{ ...return }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/returns/:id",
        description: "Radera retur",
        auth: "admin",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    title: "Fakturering",
    description: "Skapa och hantera fakturor.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/invoices",
        description: "Hämta alla fakturor",
        auth: "admin",
        response: `[{ "id": "string", "invoiceNumber": "string", "clientId": "string", "periodStart": "string", "periodEnd": "string", "totalAmount": "string", "status": "draft|sent|paid|overdue|cancelled", "items": [...], "client": {...} }]`,
      },
      {
        method: "POST",
        path: "/api/admin/invoices/generate",
        description: "Generera faktura för kund och period",
        auth: "admin",
        body: `{ "clientId": "string", "periodStart": "string (YYYY-MM-DD)", "periodEnd": "string (YYYY-MM-DD)" }`,
        response: `{ ...invoice, items: [...] }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/invoices/:id/status",
        description: "Uppdatera fakturastatus",
        auth: "admin",
        body: `{ "status": "draft|sent|paid|overdue|cancelled" }`,
        response: `{ ...invoice }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/invoices/:id",
        description: "Redigera faktura",
        auth: "admin",
        body: `{ "items?": [{ "id?": "string", "productName": "string", "quantity": number, "unitPrice": "string", "totalPrice": "string" }] }`,
        response: `{ ...invoice }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/invoices/:id",
        description: "Radera faktura",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/invoices/:id/items/:itemId",
        description: "Radera enskild fakturapost",
        auth: "admin",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    title: "Råvaror & Recept",
    description: "Hantera råvarulista, basrecept och artikelrecept.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/raw-materials",
        description: "Hämta alla råvaror",
        auth: "admin",
        response: `[{ "id": "string", "name": "string", "unit": "string", "pricePerUnit": "string", "supplier": "string" }]`,
      },
      {
        method: "POST",
        path: "/api/admin/raw-materials",
        description: "Skapa råvara",
        auth: "admin",
        body: `{ "name": "string", "unit": "string", "pricePerUnit": "string", "supplier": "string" }`,
        response: `{ ...rawMaterial }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/raw-materials/:id",
        description: "Uppdatera råvara",
        auth: "admin",
        body: `{ "name?": "string", "unit?": "string", ... }`,
        response: `{ ...rawMaterial }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/raw-materials/:id",
        description: "Radera råvara",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "GET",
        path: "/api/admin/base-recipes",
        description: "Hämta alla basrecept",
        auth: "admin",
        response: `[{ "id": "string", "name": "string", "description": "string", "ingredients": [...] }]`,
      },
      {
        method: "GET",
        path: "/api/admin/base-recipes/:id",
        description: "Hämta specifikt basrecept med ingredienser",
        auth: "admin",
        response: `{ "id": "string", "name": "string", "description": "string", "ingredients": [{ "rawMaterialId": "string", "quantity": "string", "rawMaterial": {...} }] }`,
      },
      {
        method: "POST",
        path: "/api/admin/base-recipes",
        description: "Skapa basrecept",
        auth: "admin",
        body: `{ "name": "string", "description": "string", "ingredients": [{ "rawMaterialId": "string", "quantity": "string" }] }`,
        response: `{ ...baseRecipe }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/base-recipes/:id",
        description: "Uppdatera basrecept",
        auth: "admin",
        body: `{ "name?": "string", "ingredients?": [...] }`,
        response: `{ ...baseRecipe }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/base-recipes/:id",
        description: "Radera basrecept",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "GET",
        path: "/api/admin/article-recipes",
        description: "Hämta alla artikelrecept",
        auth: "admin",
        response: `[{ "id": "string", "name": "string", "productId": "string", "baseRecipeId": "string", ... }]`,
      },
      {
        method: "POST",
        path: "/api/admin/article-recipes",
        description: "Skapa artikelrecept",
        auth: "admin",
        body: `{ "name": "string", "productId": "string", "baseRecipeId": "string", ... }`,
        response: `{ ...articleRecipe }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/article-recipes/:id",
        description: "Uppdatera artikelrecept",
        auth: "admin",
        body: `{ "name?": "string", ... }`,
        response: `{ ...articleRecipe }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/article-recipes/:id",
        description: "Radera artikelrecept",
        auth: "admin",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    title: "Inställningar",
    description: "Systemkonfiguration och inställningar.",
    endpoints: [
      {
        method: "GET",
        path: "/api/settings",
        description: "Hämta publika inställningar (t.ex. orderfönster)",
        auth: "none",
        response: `{ "key": "value", ... }`,
      },
      {
        method: "GET",
        path: "/api/settings/order-window",
        description: "Hämta orderfönster-konfiguration",
        auth: "none",
        response: `{ "isOpen": boolean, "openTime": "HH:MM", "closeTime": "HH:MM", "closedDays": [...] }`,
      },
      {
        method: "GET",
        path: "/api/admin/settings",
        description: "Hämta alla admin-inställningar",
        auth: "admin",
        response: `{ "key": "value", ... }`,
      },
      {
        method: "POST",
        path: "/api/admin/settings",
        description: "Spara inställningar",
        auth: "admin",
        body: `{ "key": "value", "key2": "value2", ... }`,
        response: `{ "message": "Settings saved" }`,
      },
    ],
  },
  {
    title: "Användare",
    description: "Hantera admin-användare.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/users",
        description: "Hämta alla admin-användare",
        auth: "admin",
        response: `[{ "id": "string", "username": "string", "displayName": "string", "role": "string" }]`,
      },
      {
        method: "POST",
        path: "/api/admin/users",
        description: "Skapa admin-användare",
        auth: "admin",
        body: `{ "username": "string", "password": "string", "displayName": "string", "role": "admin" }`,
        response: `{ ...user }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/users/:id",
        description: "Uppdatera admin-användare",
        auth: "admin",
        body: `{ "displayName?": "string", "role?": "string" }`,
        response: `{ ...user }`,
      },
      {
        method: "POST",
        path: "/api/admin/users/:id/change-password",
        description: "Ändra lösenord för admin-användare",
        auth: "admin",
        body: `{ "newPassword": "string" }`,
        response: `{ "message": "Password changed" }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/users/:id",
        description: "Radera admin-användare",
        auth: "admin",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    title: "API-kopplingar",
    description: "Hantera externa API-kopplingar.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/connections",
        description: "Hämta alla kopplingar (hemligheter maskerade)",
        auth: "admin",
        response: `[{ "id": "string", "name": "string", "baseUrl": "string", "authType": "none|api_key|bearer|basic", "isActive": boolean, "lastTestStatus": "string|null" }]`,
      },
      {
        method: "POST",
        path: "/api/admin/connections",
        description: "Skapa ny koppling",
        auth: "admin",
        body: `{ "name": "string", "baseUrl": "string", "authType": "none|api_key|bearer|basic", "apiKey?": "string", "apiKeyHeader?": "string", "bearerToken?": "string", "basicUsername?": "string", "basicPassword?": "string", "customHeaders?": {} }`,
        response: `{ ...connection }`,
      },
      {
        method: "PATCH",
        path: "/api/admin/connections/:id",
        description: "Uppdatera koppling",
        auth: "admin",
        body: `{ "name?": "string", "baseUrl?": "string", ... }`,
        response: `{ ...connection }`,
      },
      {
        method: "DELETE",
        path: "/api/admin/connections/:id",
        description: "Radera koppling",
        auth: "admin",
        response: `{ "success": true }`,
      },
      {
        method: "POST",
        path: "/api/admin/connections/:id/test",
        description: "Testa koppling (GET-anrop till baseUrl)",
        auth: "admin",
        response: `{ "success": boolean, "status": number, "statusText": "string" }`,
      },
    ],
  },
  {
    title: "Aktivitetslogg",
    description: "Hämta systemhändelser och loggposter.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/activity-logs?page=1&limit=50&category=all",
        description: "Hämta aktivitetsloggar med paginering och filtrering",
        auth: "admin",
        response: `{ "logs": [{ "id": "string", "action": "string", "category": "string", "description": "string", "performedBy": "string", "createdAt": "string" }], "total": number, "page": number, "totalPages": number }`,
      },
    ],
  },
  {
    title: "Externt API (API-nyckel)",
    description: "Endpoints för externa system (t.ex. leveransappar). Autentisering sker med X-API-Key header. Konfigurera nyckeln under Inställningar → Externt API.",
    endpoints: [
      {
        method: "GET",
        path: "/api/external/orders",
        description: "Hämta alla ordrar med artiklar",
        auth: "api_key",
        response: `[{ "id": "string", "orderNumber": number, "status": "string", "clientId": "string", "client": {...}, "items": [...] }]`,
      },
      {
        method: "GET",
        path: "/api/external/orders/:id",
        description: "Hämta en specifik order med artiklar",
        auth: "api_key",
        response: `{ "id": "string", "orderNumber": number, "status": "string", ... }`,
      },
      {
        method: "PATCH",
        path: "/api/external/orders/:id/status",
        description: "Uppdatera orderstatus. Sätter deliveryDate automatiskt vid delivered/levererad. Används av leveransappar för att markera ordrar som levererade.",
        auth: "api_key",
        body: `{ "status": "pending|confirmed|in_production|packed|delivered|levererad" }`,
        response: `{ ...order, items: [...] }`,
      },
    ],
  },
];

function CodeBlock({ code }: { code: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast({ title: "Kopierat!" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-muted rounded-md p-3 text-sm overflow-x-auto font-mono whitespace-pre-wrap break-all">
        {code}
      </pre>
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ visibility: "visible" }}
        onClick={handleCopy}
        data-testid="button-copy-code"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  );
}

function EndpointItem({ endpoint }: { endpoint: Endpoint }) {
  const [expanded, setExpanded] = useState(false);

  const authLabel = endpoint.auth === "admin" ? "Admin" : endpoint.auth === "client" ? "Kund" : endpoint.auth === "api_key" ? "API-nyckel" : "Publik";
  const authColor = endpoint.auth === "admin"
    ? "bg-purple-500/10 text-purple-600 border-purple-500/20"
    : endpoint.auth === "client"
      ? "bg-cyan-500/10 text-cyan-600 border-cyan-500/20"
      : endpoint.auth === "api_key"
        ? "bg-orange-500/10 text-orange-600 border-orange-500/20"
        : "bg-gray-500/10 text-gray-600 border-gray-500/20";

  return (
    <div className="border rounded-md" data-testid={`endpoint-${endpoint.method}-${endpoint.path.replace(/[/:]/g, '-')}`}>
      <button
        className="w-full flex items-center gap-2 p-3 text-left hover-elevate rounded-md"
        onClick={() => setExpanded(!expanded)}
        data-testid="button-toggle-endpoint"
      >
        {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <Badge variant="outline" className={`${methodColors[endpoint.method]} font-mono text-xs shrink-0`}>
          {endpoint.method}
        </Badge>
        <code className="text-sm font-mono flex-1 truncate">{endpoint.path}</code>
        <Badge variant="outline" className={`${authColor} text-xs shrink-0`}>{authLabel}</Badge>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3">
          <p className="text-sm text-muted-foreground">{endpoint.description}</p>
          {endpoint.body && (
            <div>
              <p className="text-xs font-medium mb-1 text-muted-foreground">Request body:</p>
              <CodeBlock code={endpoint.body} />
            </div>
          )}
          {endpoint.response && (
            <div>
              <p className="text-xs font-medium mb-1 text-muted-foreground">Response:</p>
              <CodeBlock code={endpoint.response} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminApiDocs() {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set([0]));

  const toggleGroup = (index: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(apiGroups.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-api-docs-title">API-dokumentation</h1>
          <p className="text-muted-foreground">Fullständig referens för alla API-endpunkter</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="button-expand-all">
            Visa alla
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="button-collapse-all">
            Dölj alla
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Allmänt</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-medium mb-1">Bas-URL</p>
            <CodeBlock code={`${window.location.origin}/api`} />
          </div>
          <div>
            <p className="font-medium mb-1">Autentisering</p>
            <p className="text-muted-foreground">
              API:et använder cookie-baserade sessioner. Logga in via <code className="bg-muted px-1 rounded">/api/admin-auth/login</code> eller <code className="bg-muted px-1 rounded">/api/client-auth/login</code> för att få en sessionscookie. Skicka sedan cookien med alla efterföljande anrop.
            </p>
          </div>
          <div>
            <p className="font-medium mb-1">Content-Type</p>
            <p className="text-muted-foreground">
              Alla request bodies ska skickas som <code className="bg-muted px-1 rounded">application/json</code>.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20 text-xs">Admin</Badge>
              <span className="text-xs text-muted-foreground">Kräver admin-session</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600 border-cyan-500/20 text-xs">Kund</Badge>
              <span className="text-xs text-muted-foreground">Kräver kund-session</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-gray-500/10 text-gray-600 border-gray-500/20 text-xs">Publik</Badge>
              <span className="text-xs text-muted-foreground">Ingen autentisering krävs</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {apiGroups.map((group, index) => (
        <Card key={index}>
          <CardHeader
            className="cursor-pointer hover-elevate rounded-t-lg"
            onClick={() => toggleGroup(index)}
            data-testid={`button-toggle-group-${index}`}
          >
            <div className="flex items-center gap-2">
              {expandedGroups.has(index) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <CardTitle className="text-base">{group.title}</CardTitle>
              <Badge variant="secondary" className="ml-auto">{group.endpoints.length}</Badge>
            </div>
            <p className="text-sm text-muted-foreground pl-6">{group.description}</p>
          </CardHeader>
          {expandedGroups.has(index) && (
            <CardContent className="space-y-2">
              {group.endpoints.map((endpoint, eIdx) => (
                <EndpointItem key={eIdx} endpoint={endpoint} />
              ))}
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}
